(function () {
  if (customElements.get('ce-announcement-bar') !== undefined) {
    return;
  }

  class AnnouncementBar extends HTMLParsedElement {
    constructor() {
      super();

      this.queuedAttributeChangedCallbackParams = [];

      this.selectors = {
        announcementList: '[data-announcement-bar-announcement-list]',
        buttonNavigation: '[data-announcement-bar-button-navigation]',
        progressBar: '[data-announcement-bar-progress-bar]',
      };

      this.duration = parseInt(this.getAttribute('duration'));
      this.total = parseInt(this.getAttribute('total'));

      this.elements = null;
      this.progressBarAnimation = null;

      this._handleMouseEnter = this._handleMouseEnter.bind(this);
      this._handleMouseLeave = this._handleMouseLeave.bind(this);
      this._handleFocusIn = this._handleFocusIn.bind(this);
      this._handleFocusOut = this._handleFocusOut.bind(this);
      this._handleClickButtonNavigation = this._handleClickButtonNavigation.bind(this);
    }

    get currentSlide() {
      return parseInt(this.getAttribute('current-slide'));
    }

    set currentSlide(value) {
      this.setAttribute('current-slide', value);
    }

    get playState() {
      return this.getAttribute('play-state');
    }

    set playState(value) {
      this.setAttribute('play-state', value);
    }

    static get observedAttributes() {
      return ['current-slide', 'play-state'];
    }

    attributeChangedCallback(...params) {
      if (!this.parsed) {
        this.queuedAttributeChangedCallbackParams.push(params);
        return;
      }

      const [name, oldValue, newValue] = params;

      if (oldValue === newValue) {
        return;
      }

      switch (name) {
        case 'current-slide':
          if (newValue !== null) {
            const previous = parseInt(oldValue);
            const next = parseInt(newValue);

            if (previous === this.total && next === 0) {
              this.elements.announcementList.classList.replace('transition-transform', 'transition-none');
              this.style.setProperty('--current-slide', next);

              setTimeout(() => {
                this.elements.announcementList.classList.replace('transition-none', 'transition-transform');
                this.style.setProperty('--current-slide', next + 1);

                this.currentSlide = next + 1;
              });

              break;
            }

            if (previous === 0 && next === this.total) {
              this.elements.announcementList.classList.replace('transition-transform', 'transition-none');
              this.style.setProperty('--current-slide', next);

              setTimeout(() => {
                this.elements.announcementList.classList.replace('transition-none', 'transition-transform');
                this.style.setProperty('--current-slide', next - 1);

                this.currentSlide = next - 1;
              });

              break;
            }

            this.elements.announcementList.classList.replace('transition-none', 'transition-transform');
            this.style.setProperty('--current-slide', next);

            this._createProgressBarAnimation();
          }

          break;
        case 'play-state':
          if (newValue !== null) {
            switch (newValue) {
              case 'running':
                this.progressBarAnimation?.play();
                this.elements.announcementList.setAttribute('aria-live', 'off');
                break;
              case 'paused':
                this.progressBarAnimation?.pause();
                this.elements.announcementList.setAttribute('aria-live', 'polite');
                break;
              case 'disabled':
                this.progressBarAnimation?.cancel();
                this.elements.announcementList.setAttribute('aria-live', 'polite');
                break;
            }
          }

          break;
      }
    }

    parsedCallback() {
      this.elements = {
        announcementList: this.querySelector(this.selectors.announcementList),
        buttonsNavigation: this.querySelectorAll(this.selectors.buttonNavigation),
        progressBar: this.querySelector(this.selectors.progressBar),
      };

      if (this.playState !== 'disabled') {
        this.addEventListener('mouseenter', this._handleMouseEnter);
        this.addEventListener('mouseleave', this._handleMouseLeave);
        this.addEventListener('focusin', this._handleFocusIn);
        this.addEventListener('focusout', this._handleFocusOut);
      }

      for (let buttonNavigation of this.elements.buttonsNavigation) {
        buttonNavigation.addEventListener('click', this._handleClickButtonNavigation);
      }

      for (let params of this.queuedAttributeChangedCallbackParams) {
        this.attributeChangedCallback(...params);
      }
    }

    disconnectedCallback() {
      if (this.playState !== 'disabled') {
        this.removeEventListener('mouseenter', this._handleMouseEnter);
        this.removeEventListener('mouseleave', this._handleMouseLeave);
        this.removeEventListener('focusin', this._handleFocusIn);
        this.removeEventListener('focusout', this._handleFocusOut);
      }

      for (let buttonNavigation of this.elements.buttonsNavigation) {
        buttonNavigation.removeEventListener('click', this._handleClickButtonNavigation);
      }
    }

    _createProgressBarAnimation() {
      this.progressBarAnimation?.cancel();

      const keyframes = [];
      const options = { duration: this.duration };

      this.progressBarAnimation = this.elements.progressBar.animate(keyframes, options);

      this.progressBarAnimation.addEventListener('finish', () => {
        this._navigateToNext();
      });

      if (this.playState !== 'running') {
        this.progressBarAnimation.pause();
      }
    }

    _navigateToPrevious() {
      if (this.currentSlide === 0) {
        this.currentSlide = this.total;
      } else {
        this.currentSlide = this.currentSlide - 1;
      }
    }

    _navigateToNext() {
      if (this.currentSlide === this.total) {
        this.currentSlide = 0;
      } else {
        this.currentSlide = this.currentSlide + 1;
      }
    }

    _handleMouseEnter() {
      this.hasMouseFocus = true;

      if (this.playState !== 'disabled') {
        this.playState = 'paused';
      }
    }

    _handleMouseLeave() {
      this.hasMouseFocus = false;

      if (document.activeElement.hasAttribute('data-announcement-bar-button-navigation')) {
        document.activeElement.blur();
      }

      if (this.playState !== 'disabled' && !this.hasKeyboardFocus) {
        this.playState = 'running';
      }
    }

    _handleFocusIn() {
      this.hasKeyboardFocus = true;

      if (this.playState !== 'disabled') {
        this.playState = 'paused';
      }
    }

    _handleFocusOut() {
      this.hasKeyboardFocus = false;

      if (this.playState !== 'disabled' && !this.hasMouseFocus) {
        this.playState = 'running';
      }
    }

    _handleClickButtonNavigation(event) {
      const button = event.target.closest(this.selectors.buttonNavigation);

      if (button.dataset.action === 'previous') {
        this._navigateToPrevious();
      } else {
        this._navigateToNext();
      }
    }
  }

  customElements.define('ce-announcement-bar', AnnouncementBar);
})();
